import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import {
    ThemeProvider,
    CssBaseline,
    Container,
    Paper,
    Divider,
    Box,
    Alert,
    Card,
    CardContent,
    TextField,
    Button,
    MenuItem,
    Select,
    FormControl,
    InputAdornment,
    Switch,
    Typography,
} from '@mui/material';

import ImagePicker from '../imagepicker/ImagePicker';
import { createBrand, listBrands as apiListBrands } from '../../api';

import { productFormTheme } from './utils/ProductForm.theme';
import { makeInitialSnapshot, normalizeHexColor, toGrams, toKey, toSingleKey } from './utils/ProductForm.utils';

import Header from './components/Header';
import FieldLabel from './components/FieldLabel';
import BrandCreateDialog from './components/BrandCreateDialog';
import ConfirmLeaveDialog from './components/ConfirmLeaveDialog';

import s from './styles/ProductForm.module.scss';

export default function ProductForm({ initialValues, onSubmit }) {
    const [title, setTitle] = useState('');
    const [brandId, setBrandId] = useState('');
    const [flavor, setFlavor] = useState('');
    const [flavorColor, setFlavorColor] = useState('#E8EEF7');
    const [weightValue, setWeightValue] = useState('');
    const [weightUnit, setWeightUnit] = useState('g');
    const [productImage, setProductImage] = useState('');
    const [isAvailable, setIsAvailable] = useState(true);
    const [descriptionHtml, setDescriptionHtml] = useState('');

    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');
    const [confirmOpen, setConfirmOpen] = useState(false);

    const initialRef = useRef(makeInitialSnapshot(initialValues || {}));
    const imageTouchedRef = useRef(false);

    // ✅ guard від зайвих reset-ів (коли батько ререндерить/перезавантажує список)
    const initKeyRef = useRef('');

    const [brands, setBrands] = useState([]);
    const [brandModalOpen, setBrandModalOpen] = useState(false);
    const [newBrandName, setNewBrandName] = useState('');
    const [newBrandLogo, setNewBrandLogo] = useState('');

    /* ================= INIT (NO RESET BUG) ================= */

    useEffect(() => {
        const key = initialValues?.id
            ? `id:${String(initialValues.id)}`
            : `new:${String(initialValues?.brandId || '')}`;

        if (initKeyRef.current === key) return;
        initKeyRef.current = key;

        const snap = makeInitialSnapshot(initialValues || {});
        initialRef.current = snap;

        setTitle(snap.title);
        setBrandId(snap.brandId);
        setFlavor(snap.flavor);
        setFlavorColor(normalizeHexColor(snap.flavorColor));
        setWeightValue(snap.weightValue);
        setWeightUnit(snap.weightUnit);
        setProductImage(snap.productImage);
        setIsAvailable(snap.isAvailable);
        setDescriptionHtml(snap.descriptionHtml);
        imageTouchedRef.current = false;
    }, [initialValues]);

    /* ================= BRANDS ================= */

    useEffect(() => {
        let alive = true;

        (async () => {
            try {
                const arr = await apiListBrands();
                if (!alive) return;
                setBrands(Array.isArray(arr) ? arr : []);
            } catch {
                if (!alive) return;
                setBrands([]);
            }
        })();

        return () => {
            alive = false;
        };
    }, []);

    /* ================= DIRTY ================= */

    const current = useMemo(
        () => ({
            title,
            brandId,
            flavor,
            flavorColor,
            weightValue,
            weightUnit,
            productImage,
            isAvailable,
            descriptionHtml,
        }),
        [title, brandId, flavor, flavorColor, weightValue, weightUnit, productImage, isAvailable, descriptionHtml]
    );

    const isDirty = JSON.stringify(current) !== JSON.stringify(initialRef.current);

    /* ================= NAV (BACK/CANCEL) ================= */

    const requestLeave = useCallback(() => {
        if (isDirty) setConfirmOpen(true);
        else window.history.back();
    }, [isDirty]);

    const forceLeave = useCallback(() => {
        setConfirmOpen(false);
        window.history.back();
    }, []);

    /* ================= SUBMIT ================= */

    async function handleSubmit(e) {
        e.preventDefault();
        setErr('');
        setBusy(true);

        try {
            const grams = toGrams(weightValue, weightUnit);
            const productKey = toKey(productImage);

            const payload = {
                brandId: String(brandId),
                name: String(title).trim(),
                description: descriptionHtml || '',
                flavor,
                flavorColor: normalizeHexColor(flavorColor),
                weightGrams: grams,
                isAvailable,
            };
            if (productKey) payload.images = [productKey];
            else if (imageTouchedRef.current) payload.images = [];

            await onSubmit?.(payload);

            // ✅ після успішного save — оновлюємо snapshot
            const newSnap = {
                ...current,
                productImage: productKey ? productKey : '',
                weightValue,
                weightUnit,
            };

            initialRef.current = newSnap;
        } catch (e2) {
            setErr(e2?.message || 'Помилка збереження');
        } finally {
            setBusy(false);
        }
    }

    /* ================= BRAND CREATE ================= */

    async function handleCreateBrand() {
        try {
            const payload = {
                name: String(newBrandName || '').trim(),
                // ✅ FIX: backend brand має поле `image`, не `logoKey`
                image: toKey(newBrandLogo) || null,
            };

            if (!payload.name) throw new Error('Вкажіть назву бренду');

            const b = await createBrand(payload);

            // ✅ бектест: createBrand повертає brand (id/key/name/image)
            setBrands((prev) => [
                ...prev,
                { id: b.id, key: b.key, name: b.name, image: b.image || null, productCount: b.productCount ?? 0 },
            ]);

            setBrandId(b.id);
            setBrandModalOpen(false);
            setNewBrandName('');
            setNewBrandLogo('');
        } catch (e) {
            alert(e?.message || 'Не вдалося створити бренд');
        }
    }

    /* ================= RENDER ================= */

    return (
        <ThemeProvider theme={productFormTheme}>
            <CssBaseline />

            <Container maxWidth="xl" className={s.screen}>
                <Header flavor={flavor} flavorColor={flavorColor} isAvailable={isAvailable} onBack={requestLeave} />

                <Paper elevation={0} className={s.card}>
                    <Box component="form" onSubmit={handleSubmit} noValidate className={s.layout}>
                        {/* LEFT */}
                        <div className={s.colMain}>
                            <Card elevation={0} className={s.section}>
                                <CardContent className={s.sectionContent}>
                                    <Typography variant="subtitle1" className={s.sectionTitle}>
                                        Основне
                                    </Typography>

                                    <div className={s.grid2}>
                                        <div className={s.field}>
                                            <FieldLabel hint="Назва, як в каталозі">Назва продукту</FieldLabel>
                                            <TextField
                                                fullWidth
                                                multiline
                                                minRows={2}
                                                value={title}
                                                onChange={(e) => setTitle(e.target.value)}
                                                required
                                                placeholder="Наприклад: Cattos Adult Beef 415g"
                                            />
                                        </div>

                                        <div className={s.field}>
                                            <FieldLabel hint="Привʼязка до бренду">Бренд</FieldLabel>
                                            <FormControl fullWidth>
                                                <Select value={brandId} onChange={(e) => setBrandId(e.target.value)} displayEmpty>
                                                    <MenuItem value="">Оберіть бренд</MenuItem>
                                                    {brands.map((b) => (
                                                        <MenuItem key={b.id} value={b.id}>
                                                            {b.name}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>

                                            <div className={s.inlineActions}>
                                                <Button variant="outlined" onClick={() => setBrandModalOpen(true)}>
                                                    + Новий бренд
                                                </Button>
                                            </div>
                                        </div>
                                    </div>

                                    <Divider className={s.divider} />

                                    <div className={s.grid2}>
                                        <div className={s.field}>
                                            <FieldLabel hint="Наприклад: Яловичина, Курка">Смак</FieldLabel>
                                            <TextField
                                                fullWidth
                                                value={flavor}
                                                onChange={(e) => setFlavor(e.target.value)}
                                                placeholder="Яловичина"
                                            />
                                        </div>

                                        <div className={s.field}>
                                            <FieldLabel hint="Колір чіпа смаку">Колір смаку</FieldLabel>
                                            <div className={s.colorRow}>
                                                <div className={s.colorInputWrap}>
                                                    <input
                                                        className={s.colorInput}
                                                        type="color"
                                                        value={normalizeHexColor(flavorColor)}
                                                        onChange={(e) => setFlavorColor(normalizeHexColor(e.target.value))}
                                                        aria-label="Flavor color"
                                                    />
                                                </div>
                                                <TextField
                                                    fullWidth
                                                    value={flavorColor}
                                                    onChange={(e) => setFlavorColor(e.target.value)}
                                                    onBlur={() => setFlavorColor((prev) => normalizeHexColor(prev))}
                                                    placeholder="#E8EEF7"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div className={s.grid2}>
                                        <div className={s.field}>
                                            <FieldLabel hint="Вага в грамах або кілограмах">Вага</FieldLabel>
                                            <TextField
                                                fullWidth
                                                value={weightValue}
                                                onChange={(e) => setWeightValue(e.target.value)}
                                                inputMode="decimal"
                                                placeholder="415"
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <FormControl variant="standard" className={s.unitSelectWrap}>
                                                                <Select
                                                                    value={weightUnit}
                                                                    onChange={(e) => setWeightUnit(e.target.value)}
                                                                    className={s.unitSelect}
                                                                >
                                                                    <MenuItem value="g">g</MenuItem>
                                                                    <MenuItem value="kg">kg</MenuItem>
                                                                </Select>
                                                            </FormControl>
                                                        </InputAdornment>
                                                    ),
                                                }}
                                            />
                                            <Typography variant="caption" className={s.helpText}>
                                                Збережеться як <b>{toGrams(weightValue, weightUnit)}</b> grams
                                            </Typography>
                                        </div>

                                        <div className={s.field}>
                                            <FieldLabel hint="Показувати в каталозі">Доступність</FieldLabel>
                                            <div className={s.switchRow}>
                                                <Switch checked={isAvailable} onChange={(e) => setIsAvailable(e.target.checked)} />
                                                <Typography variant="body2" className={s.switchText}>
                                                    {isAvailable ? 'В наявності' : 'Немає в наявності'}
                                                </Typography>
                                            </div>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>

                            <Card elevation={0} className={s.section}>
                                <CardContent className={s.sectionContent}>
                                    <Typography variant="subtitle1" className={s.sectionTitle}>
                                        Опис
                                    </Typography>

                                    <FieldLabel hint="Введи текст">Опис (HTML/текст)</FieldLabel>
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={8}
                                        value={descriptionHtml}
                                        onChange={(e) => setDescriptionHtml(e.target.value)}
                                        placeholder="<p>Опис товару...</p>"
                                    />
                                </CardContent>
                            </Card>

                            {err ? (
                                <Alert severity="error" className={s.alert}>
                                    {err}
                                </Alert>
                            ) : null}

                            <div className={s.actionsSticky}>
                                <div className={s.actionsLeft}>
                                    {isDirty ? (
                                        <Typography variant="body2" className={s.dirtyText}>
                                            Є незбережені зміни
                                        </Typography>
                                    ) : (
                                        <Typography variant="body2" className={s.cleanText}>
                                            Зміни збережено / немає змін
                                        </Typography>
                                    )}
                                </div>

                                <div className={s.actions}>
                                    <Button variant="outlined" onClick={requestLeave}>
                                        Відмінити
                                    </Button>

                                    <Button type="submit" variant="contained" disabled={busy || !brandId}>
                                        {busy ? 'Збереження…' : 'Зберегти'}
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* RIGHT */}
                        <div className={s.colSide}>
                            <div className={s.sticky}>
                                <Card elevation={0} className={s.section}>
                                    <CardContent className={s.sectionContent}>
                                        <Typography variant="subtitle1" className={s.sectionTitle}>
                                            Зображення
                                        </Typography>

                                        <ImagePicker
                                            value={productImage ? [productImage] : []}
                                            onChange={(updater) => {
                                                setProductImage((prev) => {
                                                    const base = prev ? [prev] : [];
                                                    const next = typeof updater === 'function'
                                                        ? updater(base)
                                                        : updater;
                                                    if (next == null) return prev;
                                                    const arr = Array.isArray(next) ? next : base;
                                                    if (arr.length === 0 && base.length > 0) imageTouchedRef.current = true;
                                                    if (arr.length > 0) imageTouchedRef.current = true;
                                                    return toSingleKey(arr);
                                                });
                                            }}
                                            single
                                            folder="products"
                                            libraryPrefix="products"
                                        />

                                        <div className={s.metaBox}>
                                            <Typography variant="caption" className={s.helpText}>
                                                Key: <span className={s.mono}>{productImage || '—'}</span>
                                            </Typography>
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card elevation={0} className={s.section}>
                                    <CardContent className={s.sectionContent}>
                                        <Typography variant="subtitle1" className={s.sectionTitle}>
                                            Підсумок
                                        </Typography>

                                        <div className={s.summaryLine}>
                                            <span className={s.summaryKey}>Бренд</span>
                                            <span className={s.summaryVal}>
                        {brands.find((b) => String(b.id) === String(brandId))?.name || '—'}
                      </span>
                                        </div>

                                        <div className={s.summaryLine}>
                                            <span className={s.summaryKey}>Вага</span>
                                            <span className={s.summaryVal}>
                        {weightValue ? `${weightValue}${weightUnit}` : '—'}{' '}
                                                {weightValue ? `(=${toGrams(weightValue, weightUnit)}g)` : ''}
                      </span>
                                        </div>

                                        <div className={s.summaryLine}>
                                            <span className={s.summaryKey}>Смак</span>
                                            <span className={s.summaryVal}>{flavor || '—'}</span>
                                        </div>

                                        <div className={s.summaryLine}>
                                            <span className={s.summaryKey}>Статус</span>
                                            <span className={s.summaryVal}>{isAvailable ? 'В наявності' : 'Немає'}</span>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    </Box>
                </Paper>

                {/* CREATE BRAND MODAL */}
                <BrandCreateDialog
                    open={brandModalOpen}
                    onClose={() => setBrandModalOpen(false)}
                    newBrandName={newBrandName}
                    setNewBrandName={setNewBrandName}
                    newBrandLogo={newBrandLogo}
                    setNewBrandLogo={setNewBrandLogo}
                    handleCreateBrand={handleCreateBrand}
                    toSingleKey={toSingleKey}
                />

                {/* CONFIRM LEAVE */}
                <ConfirmLeaveDialog open={confirmOpen} onClose={() => setConfirmOpen(false)} onLeave={forceLeave} />
            </Container>
        </ThemeProvider>
    );
}
